import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Sparkles, Shield, Search, KeyRound, Eye } from "lucide-react";
import PermissionEditor from "@/components/PermissionEditor";

const ROLES = ["admin", "manager", "salesperson", "service", "marketing", "viewer"];

// 29.docx — admin-controlled Contact Visibility / Shared With. The selected
// users can automatically see ALL Leads & Contacts created by this user.
function VisibilityEditor({ user, allUsers, open, onOpenChange, onSaved }) {
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && user) setSel(user.shared_with || []); }, [open, user]);
  if (!user) return null;
  const others = allUsers.filter((u) => u.id !== user.id && u.is_active);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { shared_with: sel });
      toast.success(`Visibility updated — ${sel.length ? `${sel.length} user${sel.length === 1 ? "" : "s"} can` : "no one else can"} see ${user.name}'s Leads & Contacts`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="visibility-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Contact Visibility</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Choose who can see <strong className="text-foreground">{user.name}</strong>'s Leads and Contacts.
          Records stay single — no duplicates — and "Created by" always shows {user.name}.
        </p>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="visibility-user-list">
          {others.length === 0 && <p className="text-xs text-muted-foreground">No other active users.</p>}
          {others.map((u) => {
            const on = sel.includes(u.id);
            return (
              <button key={u.id} type="button" onClick={() => toggle(u.id)}
                className={`w-full flex items-center justify-between rounded-2xl border px-3 py-2 text-left transition-colors ${on ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"}`}
                data-testid={`visibility-toggle-${u.id}`}>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold truncate">{u.name}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">{u.email} · {u.role}</span>
                </span>
                <span className={`shrink-0 text-[11px] font-bold ${on ? "text-primary" : "text-muted-foreground"}`}>{on ? "✓ Can see" : "No access"}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Visibility: <strong>{[user.name, ...others.filter((u) => sel.includes(u.id)).map((u) => u.name)].join(" + ")}</strong>{sel.length === 0 ? " only" : ""} · Admin always sees everything.
        </p>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-2xl tap" onClick={save} disabled={busy} data-testid="visibility-save">{busy ? "Saving…" : "Save visibility"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One-shot dialog that resets a single user's password AND clears their
// brute-force lockout + revokes every active session. This is the "Arun
// can't log in, please fix" workflow that ops keeps hitting.
function ResetPasswordDialog({ user, open, onOpenChange, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPw(""); }, [open]);
  if (!user) return null;
  const submit = async () => {
    if (!pw || pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset. ${data.sessions_revoked || 0} session(s) signed out.`);
      onDone?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Reset password</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Setting a new password for <strong>{user.name}</strong> ({user.email}). This also clears any
            brute-force lockout and signs out all their existing sessions.
          </p>
          <div>
            <Label className="text-xs font-semibold">New password *</Label>
            <Input
              className="rounded-2xl h-11 mt-1.5"
              type="text"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 6 characters"
              data-testid="reset-password-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">Share it with the user privately. They can change it from their profile after logging in.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-2xl" data-testid="reset-password-confirm">
            {busy ? "Resetting…" : "Reset password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewUserDialog({ open, onOpenChange, onSaved, managers }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "salesperson", department: "", reporting_manager_id: "", is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ email: "", name: "", password: "", role: "salesperson", department: "", reporting_manager_id: "", is_active: true }); }, [open]);

  const submit = async () => {
    if (!form.email || !form.name) { toast.error("Email and name required"); return; }
    setSaving(true);
    try {
      await api.post("/users", { ...form, password: form.password || undefined, reporting_manager_id: form.reporting_manager_id || null });
      toast.success("User created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">New user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold">Name *</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
          <div><Label className="text-xs font-semibold">Email *</Label><Input className="rounded-2xl h-11 mt-1.5" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
          <div><Label className="text-xs font-semibold">Password (leave blank for Google-only)</Label><Input className="rounded-2xl h-11 mt-1.5" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
          <div><Label className="text-xs font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-2xl">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">Reporting manager</Label>
            <Select value={form.reporting_manager_id || "__none"} onValueChange={(v) => setForm({ ...form, reporting_manager_id: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-2xl h-11 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="rounded-2xl"><SelectItem value="__none">None</SelectItem>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs font-semibold">Department</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="user-save-button">{saving ? "Saving…" : "Add user"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [permEditing, setPermEditing] = useState(null);
  const [visEditing, setVisEditing] = useState(null);
  const [pwReset, setPwReset] = useState(null);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/users");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const update = async (u, patch) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They will lose access immediately. (User account is preserved.)`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deactivated"); load(); } catch (err) { toast.error(fmtError(err)); }
  };

  const hardDelete = async (u) => {
    if (!window.confirm(`Permanently DELETE ${u.email}?\n\nThis cannot be undone. The user record and all their sessions will be wiped.\n\nUse this for test/junk users only.`)) return;
    try { await api.delete(`/users/${u.id}?hard=true`); toast.success("User deleted"); load(); } catch (err) { toast.error(fmtError(err)); }
  };

  const forceLogout = async (u) => {
    if (!window.confirm(`Force sign-out all sessions for ${u.email}?`)) return;
    try { await api.post(`/admin/users/${u.id}/force-logout`); toast.success("All sessions revoked"); } catch (err) { toast.error(fmtError(err)); }
  };

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-demo");
      toast.success(`Seeded ${data.created.length} demo users`);
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setSeeding(false); }
  };

  const managers = rows.filter((u) => u.role === "manager" && u.is_active);

  const needle = q.trim().toLowerCase();
  const filteredRows = rows.filter((u) => {
    if (roleFilter && u.role !== roleFilter) return false;
    if (!needle) return true;
    return (
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.department || "").toLowerCase().includes(needle)
    );
  });

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1200px] mx-auto">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">User management.</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDemo} disabled={seeding} className="rounded-2xl tap" data-testid="seed-demo-button"><Sparkles className="h-4 w-4 mr-1" /> Seed demo team</Button>
          <Button onClick={() => setOpen(true)} className="rounded-2xl tap" data-testid="new-user-button"><Plus className="h-4 w-4 mr-1" /> New user</Button>
        </div>
      </header>

      <Card className="rounded-3xl border-border/60 shadow-soft mb-3">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or department…"
              className="pl-10 h-11 rounded-2xl bg-muted/40"
              data-testid="user-search-input"
            />
          </div>
          <Select value={roleFilter || "__all"} onValueChange={(v) => setRoleFilter(v === "__all" ? "" : v)}>
            <SelectTrigger className="rounded-2xl h-11 sm:w-44" data-testid="user-role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
            <SelectContent className="rounded-2xl">
              <SelectItem value="__all">All roles</SelectItem>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-3 space-y-2">
          {filteredRows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No users match your search.</p>
          ) : filteredRows.map((u) => {
            const customCount = (u.perm_overrides || []).length + (u.perm_revoked || []).length;
            return (
            <div
              key={u.id}
              className="rounded-2xl border border-border/60 hover:bg-muted/30 transition p-4 lg:grid lg:grid-cols-12 lg:items-center lg:gap-3 flex flex-col gap-3"
              data-testid={`user-row-${u.id}`}
            >
              {/* Identity */}
              <div className="lg:col-span-3 min-w-0">
                <p className="font-semibold text-sm truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>

              {/* Role + department + active — single row on mobile */}
              <div className="flex items-center gap-2 flex-wrap lg:contents">
                <div className="lg:col-span-2 w-32 lg:w-auto">
                  <Select value={u.role} onValueChange={(v) => update(u, { role: v })}>
                    <SelectTrigger className="rounded-2xl h-9 text-xs w-full" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-1 text-xs text-muted-foreground hidden lg:block">{u.department || "—"}</div>
                <div className="lg:col-span-2 flex items-center gap-2 ml-auto lg:ml-0">
                  <Switch checked={!!u.is_active} onCheckedChange={(c) => update(u, { is_active: c })} data-testid={`user-active-${u.id}`} />
                  <span className="text-xs">{u.is_active ? "Active" : "Inactive"}</span>
                </div>
              </div>

              {/* Actions — full row below on mobile */}
              <div className="lg:col-span-4 flex justify-stretch lg:justify-end items-center gap-1.5 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl tap flex-1 lg:flex-none"
                  onClick={() => setVisEditing(u)}
                  data-testid={`user-visibility-${u.id}`}
                  title="Contact Visibility — choose who can see this user's Leads & Contacts"
                >
                  <Eye className="h-3 w-3 mr-1" />
                  Visibility
                  {(u.shared_with || []).length > 0 && (
                    <Badge variant="outline" className="ml-1.5 bg-sky-50 text-sky-800 border-sky-200 text-[9px] px-1 py-0">
                      {(u.shared_with || []).length}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl tap flex-1 lg:flex-none"
                  onClick={() => setPermEditing(u)}
                  data-testid={`user-permissions-${u.id}`}
                  title="Customize per-user permissions"
                >
                  <Shield className="h-3 w-3 mr-1" />
                  Permissions
                  {customCount > 0 && (
                    <Badge variant="outline" className="ml-1.5 bg-amber-50 text-amber-800 border-amber-200 text-[9px] px-1 py-0">
                      {customCount}
                    </Badge>
                  )}
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex-1 lg:flex-none" onClick={() => setPwReset(u)} data-testid={`user-reset-password-${u.id}`} title="Reset password (also clears lockout & revokes sessions)">
                  <KeyRound className="h-3 w-3 mr-1" /> Reset password
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex-1 lg:flex-none" onClick={() => forceLogout(u)} data-testid={`user-force-logout-${u.id}`} title="Force sign-out all sessions">
                  Force out
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl flex-1 lg:flex-none" onClick={() => deactivate(u)} data-testid={`user-deactivate-${u.id}`}>Deactivate</Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl flex-1 lg:flex-none text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200/60"
                  onClick={() => hardDelete(u)}
                  data-testid={`user-delete-${u.id}`}
                  title="Permanently delete user (cannot be undone)"
                >
                  Delete
                </Button>
              </div>
            </div>
            );
          })}
        </CardContent>
      </Card>

      <NewUserDialog open={open} onOpenChange={setOpen} onSaved={load} managers={managers} />
      <ResetPasswordDialog
        user={pwReset}
        open={!!pwReset}
        onOpenChange={(v) => !v && setPwReset(null)}
        onDone={load}
      />
      <PermissionEditor
        user={permEditing}
        open={!!permEditing}
        onOpenChange={(v) => !v && setPermEditing(null)}
        onSaved={load}
      />
      <VisibilityEditor
        user={visEditing}
        allUsers={rows}
        open={!!visEditing}
        onOpenChange={(v) => !v && setVisEditing(null)}
        onSaved={load}
      />
    </div>
  );
}
